import { Camera, Crown, Headphones, ImageIcon, MonitorUp, Radio, Settings, Shield, Users, Video } from 'lucide-react';
import { useState } from 'react';
import type { ConversationSummary, Profile } from '../types';
import { relativeLastSeen } from '../lib/format';
import { getSupabase } from '../lib/supabase';
import { getConversationAvatar, getConversationTitle } from '../lib/conversations';
import { uploadConversationIcon } from '../lib/storage';
import Avatar from './Avatar';

export default function ProfilePanel({
  profile,
  conversation,
  onlineIds,
  onOpenSettings,
  onProfileClick,
  onConversationChanged,
}: {
  profile: Profile;
  conversation: ConversationSummary | null;
  onlineIds: Set<string>;
  onOpenSettings: () => void;
  onProfileClick: (profile: Profile) => void;
  onConversationChanged: () => void;
}) {
  const [groupIconMessage, setGroupIconMessage] = useState<string | null>(null);
  const [uploadingGroupIcon, setUploadingGroupIcon] = useState(false);
  const liveSpaces = [
    { label: 'Voice Lounge', icon: Headphones, copy: '3 listening' },
    { label: 'Standup Room', icon: Video, copy: 'Ready' },
    { label: 'Screen Share', icon: MonitorUp, copy: 'Available' },
  ];
  const currentMembership = conversation?.members.find((member) => member.user_id === profile.id);
  const canEditGroupIcon =
    Boolean(conversation && conversation.type === 'group') &&
    (currentMembership?.role === 'owner' || currentMembership?.role === 'admin' || profile.role === 'admin');

  async function handleGroupIcon(file: File | null) {
    if (!file || !conversation) return;

    setUploadingGroupIcon(true);
    setGroupIconMessage(null);

    try {
      const avatarUrl = await uploadConversationIcon(conversation.id, file);
      const client = getSupabase();
      const { error } = await client.from('conversations').update({ avatar_url: avatarUrl }).eq('id', conversation.id);

      if (error) throw error;

      setGroupIconMessage('Group icon updated.');
      onConversationChanged();
    } catch (error) {
      setGroupIconMessage(error instanceof Error ? error.message : 'Could not update group icon.');
    } finally {
      setUploadingGroupIcon(false);
    }
  }

  return (
    <aside className="relay-glass hidden h-screen w-[312px] shrink-0 border-l border-white/10 xl:flex xl:flex-col">
      <div className="border-b border-white/10 p-4">
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.035]">
          <div className="h-20 bg-[radial-gradient(circle_at_20%_15%,rgba(34,211,238,.34),transparent_32%),linear-gradient(135deg,rgba(79,140,255,.45),rgba(139,92,246,.28),rgba(5,7,13,.95))]" />
          <div className="-mt-8 px-4 pb-4">
            <div className="flex items-end justify-between gap-3">
              <Avatar name={profile.display_name} src={profile.avatar_url} online size="lg" />
              <button
                type="button"
                onClick={onOpenSettings}
                className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-black/25 text-slate-400 transition hover:bg-white/10 hover:text-white"
                aria-label="Open settings"
                title="Settings"
              >
                <Settings size={16} />
              </button>
            </div>
            <p className="mt-3 truncate text-base font-bold text-white">{profile.display_name}</p>
            <p className="truncate text-sm text-slate-500">@{profile.username}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-lg bg-relay-green/10 px-2.5 py-1 text-xs font-semibold text-relay-green">Online</span>
              <span className="rounded-lg bg-relay-cyan/10 px-2.5 py-1 text-xs font-semibold text-relay-cyan">
                {profile.role}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="relay-scrollbar flex-1 overflow-y-auto p-5">
        {conversation?.type === 'group' && (
          <section className="mb-5 rounded-2xl border border-white/10 bg-white/[0.035] p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
              <ImageIcon size={17} className="text-relay-cyan" />
              Group chat
            </div>
            <div className="flex items-center gap-3">
              <Avatar
                name={getConversationTitle(conversation, profile.id)}
                src={getConversationAvatar(conversation, profile.id)}
                online={conversation.members.some((member) => member.user_id !== profile.id && onlineIds.has(member.user_id))}
                size="lg"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-white">{getConversationTitle(conversation, profile.id)}</p>
                <p className="text-xs text-slate-500">{conversation.members.length} members</p>
              </div>
            </div>
            {canEditGroupIcon && (
              <label className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-relay-cyan/25 bg-relay-cyan/10 px-3 py-2.5 text-sm font-bold text-relay-cyan transition hover:bg-relay-cyan/15">
                <Camera size={16} />
                {uploadingGroupIcon ? 'Uploading...' : 'Set group icon'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingGroupIcon}
                  onChange={(event) => handleGroupIcon(event.target.files?.[0] ?? null)}
                />
              </label>
            )}
            {groupIconMessage && <p className="mt-2 text-xs text-relay-cyan">{groupIconMessage}</p>}
          </section>
        )}

        <section className="mb-5 rounded-2xl border border-white/10 bg-white/[0.035] p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
            <Radio size={17} className="text-relay-cyan" />
            Live spaces
          </div>
          <div className="space-y-2">
            {liveSpaces.map(({ label, icon: Icon, copy }) => (
              <button
                key={label}
                type="button"
                className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-black/15 px-3 py-2.5 text-left transition hover:bg-white/[0.06]"
              >
                <Icon size={16} className="text-slate-400" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-slate-200">{label}</span>
                  <span className="text-xs text-slate-500">{copy}</span>
                </span>
              </button>
            ))}
          </div>
        </section>

        <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
          <Users size={17} className="text-relay-cyan" />
          Members
        </div>
        {!conversation && <p className="text-sm text-slate-500">Select a conversation to see members.</p>}
        {conversation && (
          <div className="space-y-2">
            {conversation.members.map((member) => (
              <button
                key={member.user_id}
                type="button"
                onClick={() => onProfileClick(member.profile)}
                className="flex w-full items-center gap-3 rounded-lg p-2 text-left hover:bg-white/[0.04]"
              >
                <Avatar
                  name={member.profile.display_name}
                  src={member.profile.avatar_url}
                  online={onlineIds.has(member.user_id)}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">{member.profile.display_name}</p>
                  <p className="truncate text-xs text-slate-500">
                    {onlineIds.has(member.user_id) ? 'Online' : `Seen ${relativeLastSeen(member.profile.last_seen)}`}
                  </p>
                </div>
                {member.role !== 'member' && <Crown className="text-relay-cyan" size={15} />}
              </button>
            ))}
          </div>
        )}

        <section className="mt-5 rounded-2xl border border-white/10 bg-white/[0.035] p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-white">
            <Shield size={16} className="text-relay-cyan" />
            Privacy
          </div>
          <p className="text-xs leading-5 text-slate-500">
            Messages, uploads, and profile access are protected by Supabase Row Level Security.
          </p>
        </section>
      </div>
    </aside>
  );
}
